import React, { useMemo, useRef, useState } from 'react';
import './BillingAnalyticsSection.css';
import { setBillingAnalyticsParams as persistBillingAnalyticsParams } from '../../api/api';
import * as XLSX from 'xlsx';
import {
  applyFinanceInputsToBillingInputs,
  buildBillingTodTariffModel,
  computeBillingAnalytics,
  createDefaultBillingAnalyticsInputs,
  deriveBillingKvahFromUnits,
  extractPersistableBillingAnalyticsInputs,
  hasSavedBillingAnalyticsDocument,
  mapBillingFinanceInputs,
  overlaySavedBillingInputs,
  resolveUniversalSubsidyLabel,
  toBillingNumber,
} from './billingAnalyticsShared';

const POWER_FACTOR = 0.9989;
const toNumber = toBillingNumber;
const createDefaultInputs = createDefaultBillingAnalyticsInputs;
const extractPersistableInputs = extractPersistableBillingAnalyticsInputs;
const SECTION_OMIT_KEYS = ['batteryTodTariffsByBlock'];
const sectionPersistable = (raw) => extractPersistableInputs(raw, { omitKeys: SECTION_OMIT_KEYS });
const deriveKvahFromUnits = deriveBillingKvahFromUnits;

// Display formatter: preserve up to 2 decimal places for rates/calculated unit values
const format2 = (v) => {
  const n = toNumber(v);
  return Number.isFinite(n) ? n.toFixed(2) : '-';
};

// Indian number formatter (whole numbers only)
const formatIndian = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return value ?? '-';
  const int = n.toFixed(0);
  const lastThree = int.slice(-3);
  const other = int.slice(0, -3);
  const formatted = other !== '' ? other.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree : lastThree;
  return `${formatted}`;
};

const fmtMaybeIndian = (v, { empty = '-' } = {}) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return empty;
  return formatIndian(n);
};

const BillingAnalyticsSection = ({
  globalParams = {},
  annualMetrics = null,
  financeInputs = null,
  bankingSummary = null,
  opCostsOutput = null,
  pipelineName = null,
  initialParams = null,
  initialParamsLoaded = false,
  onParamsChange = null,
  includeOperationalCostInPdf = true,
  onIncludeOperationalCostInPdfChange = null,
  includeTodWiseTariffInPdf = true,
  onIncludeTodWiseTariffInPdfChange = null,
}) => {
  
  const wheelingRate = useMemo(() => {
    
    return toNumber(globalParams?.wheeling_charges);
  }, [globalParams]);

  const transmissionRate = useMemo(() => {
    
    return toNumber(globalParams?.transmission_charges_per_KWh);
  }, [globalParams]);

  const netSettledUnitsAnnual = useMemo(() => {
    return toNumber(annualMetrics?.unitsFromOA);
  }, [annualMetrics]);

  const discountRate = useMemo(() => {
    const dr = toNumber(globalParams?.discount_rate);
    if (!dr) return 0;
    return dr > 1 ? dr / 100 : dr;
  }, [globalParams]);

  const transmissionLossPerc = useMemo(() => {
    const p = toNumber(globalParams?.transmission_loss_perc);
    if (!p) return 0;
    return p > 1 ? p / 100 : p;
  }, [globalParams]);

  const wheelingLossPerc = useMemo(() => {
    const p = toNumber(globalParams?.wheeling_loss_perc);
    if (!p) return 0;
    return p > 1 ? p / 100 : p;
  }, [globalParams]);

  const [inputs, setInputs] = useState(() => createDefaultInputs());

  const [calc, setCalc] = useState(null);
  const [hydrationReady, setHydrationReady] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const [saveError, setSaveError] = useState('');
  const persistTimeoutRef = useRef(null);
  const latestInputsRef = useRef(createDefaultInputs());
  const latestPersistableInputsRef = useRef({});
  const lastSavedPayloadRef = useRef(JSON.stringify(sectionPersistable(createDefaultInputs())));
  const userInteractedRef = useRef(false);

  const syncInputRefs = React.useCallback((nextInputs) => {
    latestInputsRef.current = nextInputs;
    latestPersistableInputsRef.current = extractPersistableInputs(nextInputs);
  }, []);

  const persistableInputs = useMemo(() => sectionPersistable(inputs), [inputs]);

  const persistNow = React.useCallback(async (payload, { updateUi = true } = {}) => {
    if (!pipelineName || pipelineName === 'Unsaved_Pipeline') return;
    const normalizedPayload = sectionPersistable(payload || {});
    const serializedPayload = JSON.stringify(normalizedPayload);
    if (serializedPayload === lastSavedPayloadRef.current) {
      if (updateUi) {
        setSaveState('saved');
        setSaveError('');
      }
      return;
    }
    if (updateUi) {
      setSaveState('saving');
      setSaveError('');
    }
    try {
      await persistBillingAnalyticsParams(pipelineName, normalizedPayload);
      lastSavedPayloadRef.current = serializedPayload;
      if (updateUi) {
        setSaveState('saved');
        setSaveError('');
      }
    } catch (error) {
      if (updateUi) {
        setSaveState('error');
        setSaveError(error?.message || 'Failed to save billing analytics inputs.');
      }
    }
  }, [pipelineName]);

  React.useEffect(() => {
    syncInputRefs(inputs);
  }, [inputs, syncInputRefs]);

  React.useEffect(() => {
    if (!hydrationReady) return;
    latestPersistableInputsRef.current = persistableInputs;
    if (typeof onParamsChange === 'function') {
      onParamsChange(persistableInputs);
    }
  }, [hydrationReady, persistableInputs, onParamsChange]);

  React.useEffect(() => {
    if (!initialParamsLoaded) {
      setHydrationReady(false);
      userInteractedRef.current = false;
      return;
    }
    if (!initialParams || typeof initialParams !== 'object') {
      setHydrationReady(true);
      userInteractedRef.current = false;
      return;
    }
    const normalizedInitialParams = sectionPersistable(initialParams);
    const incomingSerialized = JSON.stringify(normalizedInitialParams);
    const latestSerialized = JSON.stringify(latestPersistableInputsRef.current || {});
    if (hydrationReady && incomingSerialized === latestSerialized) {
      return;
    }
    if (incomingSerialized && incomingSerialized === lastSavedPayloadRef.current) {
      setHydrationReady(true);
      return;
    }
    if (incomingSerialized === JSON.stringify(sectionPersistable(latestInputsRef.current || {}))) {
      setHydrationReady(true);
      return;
    }

    const hasSaved = hasSavedBillingAnalyticsDocument(initialParams);
    setInputs((prev) => {
      if (hasSaved) {
        const saved = extractPersistableInputs(initialParams);
        const next = overlaySavedBillingInputs(
          { ...createDefaultInputs(), ...saved },
          initialParams,
        );
        syncInputRefs(next);
        return next;
      }

      const next = { ...prev };
      let changed = false;
      Object.keys(normalizedInitialParams).forEach((k) => {
        if (k === 'todTariffsBySlot' && normalizedInitialParams[k] && typeof normalizedInitialParams[k] === 'object') {
          const mergedTodTariffs = { ...(prev.todTariffsBySlot || {}), ...(normalizedInitialParams.todTariffsBySlot || {}) };
          if (JSON.stringify(mergedTodTariffs) !== JSON.stringify(prev.todTariffsBySlot || {})) {
            next.todTariffsBySlot = mergedTodTariffs;
            changed = true;
          }
          return;
        }

        const currentValue = next[k];
        const isBlank =
          currentValue === '' ||
          currentValue === null ||
          currentValue === undefined;
        if (Object.prototype.hasOwnProperty.call(next, k) && isBlank && currentValue !== normalizedInitialParams[k]) {
          next[k] = normalizedInitialParams[k];
          changed = true;
        }
      });
      if (changed) {
        syncInputRefs(next);
      }
      return changed ? next : prev;
    });
    lastSavedPayloadRef.current = incomingSerialized;
    setSaveState('idle');
    setSaveError('');
    setHydrationReady(true);
    userInteractedRef.current = false;
  }, [hydrationReady, initialParams, initialParamsLoaded, syncInputRefs]);

  const todTariffModel = useMemo(
    () => buildBillingTodTariffModel({ bankingSummary, inputs }),
    [bankingSummary, inputs]
  );

  const handleTodTariffChange = (slot) => (e) => {
    const value = e?.target?.value ?? '';
    userInteractedRef.current = true;
    setInputs((prev) => {
      const next = {
        ...prev,
        wasCalculated: false,
        todTariffsBySlot: { ...(prev.todTariffsBySlot || {}), [slot]: value },
      };
      syncInputRefs(next);
      return next;
    });
    setCalc(null);
  };

  // Auto-fill mapped fields from Finance node inputs 
  const financeMapped = useMemo(() => mapBillingFinanceInputs(financeInputs), [financeInputs]);

  
  React.useEffect(() => {
    if (!financeMapped || !hydrationReady) return;
    setInputs((prev) => {
      const applied = applyFinanceInputsToBillingInputs(prev, financeInputs, initialParams);
      if (JSON.stringify(applied) === JSON.stringify(prev)) return prev;
      syncInputRefs(applied);
      return applied;
    });
  }, [financeInputs, financeMapped, hydrationReady, initialParams, syncInputRefs]);

  // Always derive KVAH from Units using power factor 0.9989
  React.useEffect(() => {
    const next = deriveKvahFromUnits(inputs.unitsConsumedUnits);
    if ((inputs.unitsConsumedKvah ?? '') === next) return;
    setInputs((prev) => {
      const synced = { ...prev, unitsConsumedKvah: next };
      syncInputRefs(synced);
      return synced;
    });
    
  }, [inputs.unitsConsumedKvah, inputs.unitsConsumedUnits, syncInputRefs]);

  // Auto-save user inputs 
  React.useEffect(() => {
    if (!hydrationReady) return;
    if (!pipelineName || pipelineName === 'Unsaved_Pipeline') return;
    if (!userInteractedRef.current) return;

    const serializedPayload = JSON.stringify(persistableInputs);
    if (serializedPayload === lastSavedPayloadRef.current) {
      return;
    }

    setSaveState('pending');
    setSaveError('');
    persistTimeoutRef.current = setTimeout(() => {
      void persistNow(persistableInputs);
    }, 800);
    return () => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
        persistTimeoutRef.current = null;
      }
    };
  }, [hydrationReady, persistNow, persistableInputs, pipelineName]);

  React.useEffect(() => {
    return () => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
        persistTimeoutRef.current = null;
      }
      const latestPayload = sectionPersistable(latestInputsRef.current || {});
      const serializedPayload = JSON.stringify(latestPayload);
      if (!userInteractedRef.current || !pipelineName || pipelineName === 'Unsaved_Pipeline' || serializedPayload === lastSavedPayloadRef.current) {
        return;
      }
      void persistNow(latestPayload, { updateUi: false });
    };
  }, [persistNow, pipelineName]);

  const handleChange = (key) => (e) => {
    const value = e.target.value;
    userInteractedRef.current = true;
    setInputs((prev) => {
      const next = { ...prev, [key]: value, wasCalculated: false };
      syncInputRefs(next);
      return next;
    });
    setCalc(null);
  };

  const calculate = React.useCallback(({ persist = true } = {}) => {
    const currentInputs = latestInputsRef.current || inputs;
    const result = computeBillingAnalytics({
      inputs: currentInputs,
      financeInputs,
      globalParams,
      annualMetrics,
      bankingSummary,
      opCostsOutput,
      savedInputs: hasSavedBillingAnalyticsDocument(initialParams) ? initialParams : null,
    });

    setCalc(result.calc);

    const nextInputs = { ...result.inputs, wasCalculated: true };
    syncInputRefs(nextInputs);
    setInputs(nextInputs);
    if (persist) {
      userInteractedRef.current = true;
      void persistNow(nextInputs);
    }
  }, [
    inputs,
    annualMetrics,
    bankingSummary,
    financeInputs,
    globalParams,
    initialParams,
    opCostsOutput,
    persistNow,
    syncInputRefs,
  ]);

  const hasPersistedInputValues = useMemo(() => (
    Object.entries(persistableInputs || {}).some(([key, value]) => {
      if (key === 'wasCalculated') return false;
      if (key === 'todTariffsBySlot') return Object.keys(value || {}).length > 0;
      return value !== null && value !== undefined && String(value).trim() !== '';
    })
  ), [persistableInputs]);

  const recalcSignature = JSON.stringify({
    inputs: sectionPersistable(inputs),
    annualGeneration: annualMetrics?.totalAnnualGeneration ?? null,
    reSupplied: annualMetrics?.reSuppliedUnits ?? null,
    bankingRows: Array.isArray(bankingSummary?.tod_summary) ? bankingSummary.tod_summary.length : 0,
    monthlyRows: Array.isArray(bankingSummary?.monthly_summary) ? bankingSummary.monthly_summary.length : 0,
    opRows: Array.isArray(opCostsOutput) ? opCostsOutput.length : (opCostsOutput ? 1 : 0),
  });
  const lastRecalcSignatureRef = useRef('');

  React.useEffect(() => {
    if (!hydrationReady || !inputs.wasCalculated || !hasPersistedInputValues) return;
    if ((inputs.unitsConsumedKvah ?? '') !== deriveKvahFromUnits(inputs.unitsConsumedUnits)) return;
    if (lastRecalcSignatureRef.current === recalcSignature) return;
    lastRecalcSignatureRef.current = recalcSignature;
    calculate({ persist: false });
  }, [
    calculate,
    hasPersistedInputValues,
    hydrationReady,
    inputs.unitsConsumedKvah,
    inputs.unitsConsumedUnits,
    inputs.wasCalculated,
    recalcSignature,
  ]);

  const handleExportExcel = () => {
    if (!calc) return;

    const now = new Date();
    const pad2 = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}_${pad2(now.getHours())}${pad2(now.getMinutes())}`;
    const safeName = String(pipelineName || 'pipeline').replace(/[^\w.-]+/g, '_');
    const fileName = `${safeName}_billing_analytics_${stamp}.xlsx`;

    const numOrEmpty = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).trim();
      if (!s) return '';
      return toNumber(s);
    };

    const mainHeaderRow1 = ['', '', 'DISCOM', '', 'OA units', '', 'EB Residual', ''];
    const mainHeaderRow2 = ['Particular', 'UOM', 'Value', 'Rate', 'Value', 'Rate', 'Value', 'Rate'];
    const mainRows = [
      ['Voltage', 'KV', inputs.voltage || '', '', '', '', '', ''],
      ['CD', 'KVA', numOrEmpty(inputs.cdKva), '', '', '', '', ''],
      ['Actual CD', 'KVA', numOrEmpty(inputs.actualCdKva), '', '', '', '', ''],
      ['Billed CD', 'KVA', numOrEmpty(inputs.billedCdKva), '', '', '', calc.I10, ''],

      ['Units consumed', 'Units', numOrEmpty(inputs.unitsConsumedUnits), '', '', '', calc.I11, ''],
      ['Units consumed', 'KVAH', numOrEmpty(inputs.unitsConsumedKvah), calc.F12, calc.G12, calc.oaUnitsRateH12, calc.I12, ''],

      ['Demand charge', 'INR', numOrEmpty(inputs.demandChargeInr), numOrEmpty(inputs.demandChargeRateF13), '', '', calc.I13, calc.J13],
      ['Wheeling charge', 'INR', calc.wheelingCharge, wheelingRate, '', '', calc.I14, wheelingRate],
      ['Energy charge', 'INR', calc.energyCharge, numOrEmpty(inputs.energyChargeRateF15), '', '', calc.I15, calc.J15],
      ['TOD', 'INR', numOrEmpty(inputs.todInr), calc.F16, '', '', calc.I16, calc.J16],
      ['FAC', 'INR', numOrEmpty(inputs.facInr), calc.F17, '', '', calc.I17, calc.J17],
      ['ED', 'INR', numOrEmpty(inputs.edInr), calc.F18, '', '', calc.I18, calc.J18],
      ['Tax on sale', 'INR', numOrEmpty(inputs.taxOnSaleInr), calc.F19, '', '', calc.I19, calc.J19],
      ['Tax Collection at Source', 'INR', numOrEmpty(inputs.tcsInr), calc.F20, '', '', numOrEmpty(inputs.ebTcsAmountI20), numOrEmpty(inputs.ebTcsRateJ20)],
      ['Bulk Consumption rebate', 'INR', numOrEmpty(inputs.bulkConsumptionRebateInr), calc.F21, '', '', numOrEmpty(inputs.ebBulkConsumptionRebateAmountI21), calc.J21],
      ['Prompt payment discount', 'INR', numOrEmpty(inputs.promptPaymentDiscountInr), calc.F22, '', '', calc.I22, calc.J22],
      ['Load factor incentive', 'INR', numOrEmpty(inputs.loadFactorIncentiveInr), calc.F23, '', '', numOrEmpty(inputs.ebLoadFactorIncentiveAmountI23), calc.J23],
      ['Incremental consumption rebate', 'INR', numOrEmpty(inputs.incrementalConsumptionRebateInr), calc.F24, '', '', numOrEmpty(inputs.ebIncrementalConsumptionRebateAmountI24), calc.J24],
      ['Regional subsidy', 'INR', numOrEmpty(inputs.regionalSubsidyInr), calc.F25, '', '', '', calc.J25],
      [resolveUniversalSubsidyLabel(financeInputs), 'INR', numOrEmpty(inputs.universalSubsidyInr), calc.universalSubsidyRate, '', '', '', calc.universalSubsidyRate],

      ['Total Power cost', 'INR', calc.totalPowerCostE26, calc.F26, calc.G26, '', calc.I26, calc.J26],
      ['Replaceable Power cost', 'INR/unit', calc.replaceablePowerCostE27, '', '', '', calc.I27, calc.J27],
      ['Savings on total power consumed', 'INR/unit', '', '', '', '', '', calc.J28],
      ['Savings on OA units', 'INR/unit', '', '', '', '', '', calc.savingsOnOaUnits],
    ];

    const todHeader = ['Time slots', 'Consumption', 'Demand Met', 'Grid consumption', 'TOD tariff (Rs/unit)'];
    const todRows = (() => {
      if (!todTariffModel?.hasData) return [['No TOD time-slot summary available (run Banking node).', '', '', '', '']];
      const rows = (todTariffModel.rows || []).map((r) => ([
        r.label,
        toNumber(r.consumption),
        toNumber(r.demandMet),
        toNumber(r.gridConsumption),
        numOrEmpty((inputs.todTariffsBySlot || {})[r.key]),
      ]));
      rows.push([
        'Total',
        toNumber(todTariffModel.totals?.totalConsumption),
        toNumber(todTariffModel.totals?.totalDemandMet),
        toNumber(todTariffModel.totals?.totalGridConsumption),
        toNumber(todTariffModel.totals?.weightedAvgTariff),
      ]);
      return rows;
    })();

    const oaOpsAoa = [
      ['Operational Cost for Open access', 'UOM', 'Charges'],
      ['Generation tariff', 'Rs/unit', numOrEmpty(inputs.generationTariffInrPerUnit)],
      ['Current Replacement Tariff', 'Rs/unit', calc.replaceablePowerCostE27],
      ['Transmission charge in', 'Rs/unit', transmissionRate],
      ['Transmission loss in Rs / unit', 'Rs/unit', toNumber(calc.oc?.transmissionLoss)],
      ['Wheeling charges in Rs unit', 'Rs/unit', wheelingRate],
      ['Wheeling losses in Rs per unit', 'Rs/unit', toNumber(calc.oc?.wheelingLoss)],
      ['Banking Charge on Banked units', 'Rs/unit', toNumber(calc.oc?.bankingCharge)],
      ['Lapse unit charge', 'Rs/unit', toNumber(calc.oc?.lapsedUnitCharge)],
      ['O&M, lease, Insurance and other expenses', 'Rs/unit', toNumber(calc.oc?.omLeaseInsuranceOther)],
      ['Delivered power cost at Consumption point', 'Rs/unit', calc.deliveredPowerCostH12],
    ];

    const wb = XLSX.utils.book_new();
    const fullAoa = [
      ['Billing Analytics Export'],
      ['Pipeline', pipelineName || ''],
      ['Exported at', now.toLocaleString()],
      ['Discount rate (decimal)', discountRate],
      ['Wheeling charges (Rs/unit)', wheelingRate],
      ['Transmission charges (Rs/unit)', transmissionRate],
      ['Transmission loss % (decimal)', transmissionLossPerc],
      ['Wheeling loss % (decimal)', wheelingLossPerc],
      ['Net settled units (annual)', netSettledUnitsAnnual],
      [],
      mainHeaderRow1,
      mainHeaderRow2,
      ...mainRows,
      [],
      ['TOD wise tariff calculation'],
      todHeader,
      ...todRows,
      [],
      ['Operational Cost for Open access'],
      ...oaOpsAoa,
    ];

    const ws = XLSX.utils.aoa_to_sheet(fullAoa);
    // Merge the grouped headers in the main table header row.
    ws['!merges'] = [
      { s: { r: 10, c: 2 }, e: { r: 10, c: 3 } }, // DISCOM
      { s: { r: 10, c: 4 }, e: { r: 10, c: 5 } }, // OA units
      { s: { r: 10, c: 6 }, e: { r: 10, c: 7 } }, // EB Residual
    ];
    ws['!cols'] = [
      { wch: 34 }, // Particular
      { wch: 10 }, // UOM
      { wch: 16 },
      { wch: 14 },
      { wch: 16 },
      { wch: 14 },
      { wch: 18 },
      { wch: 16 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Billing Analytics');

    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="billing-analytics">
      <div className="billing-analytics-toolbar">
        <button className="billing-analytics-calc-btn" onClick={calculate}>
          Calculate
        </button>
        <button
          className="billing-analytics-export-btn"
          onClick={handleExportExcel}
          disabled={!calc}
          title={!calc ? 'Click Calculate first, then export.' : 'Export Billing Analytics to Excel (.xlsx)'}
        >
          Export (Excel)
        </button>
        <div className="billing-analytics-hint">
          Fill inputs, then click <b>Calculate</b>. 
        </div>
        <div className={`billing-analytics-save-state billing-analytics-save-state-${saveState}`}>
          {saveState === 'saving' && 'Saving to pipeline...'}
          {saveState === 'pending' && 'Changes pending save...'}
          {saveState === 'saved' && 'Saved to pipeline'}
          {saveState === 'error' && (saveError || 'Failed to save billing analytics inputs.')}
        </div>
      </div>

      <div className="summary-table-wrapper">
        <table className="summary-table billing-analytics-table">
          <colgroup>
            <col style={{ width: '200px' }} />
            <col style={{ width: '55px' }} />
            <col style={{ width: '105px' }} />
            <col style={{ width: '70px' }} />
            <col style={{ width: '105px' }} />
            <col style={{ width: '70px' }} />
            <col style={{ width: '105px' }} />
            <col style={{ width: '70px' }} />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={2}></th>
              <th colSpan={2}>DISCOM</th>
              <th colSpan={2}>OA UNITS</th>
              <th colSpan={2}>EB RESIDUAL</th>
            </tr>
            <tr>
              <th>PARTICULAR</th>
              <th>UOM</th>
              <th>VALUE</th>
              <th>RATE</th>
              <th>VALUE</th>
              <th>RATE</th>
              <th>VALUE</th>
              <th>RATE</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Voltage</td>
              <td>KV</td>
              <td>
                <input className="ba-input" value={inputs.voltage} onChange={handleChange('voltage')} placeholder="e.g. 33" />
              </td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
            </tr>
            <tr>
              <td>CD</td>
              <td>KVA</td>
              <td><input className="ba-input" value={inputs.cdKva} onChange={handleChange('cdKva')} /></td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
            </tr>
            <tr>
              <td>Actual CD</td>
              <td>KVA</td>
              <td><input className="ba-input" value={inputs.actualCdKva} onChange={handleChange('actualCdKva')} /></td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
            </tr>
            <tr>
              <td>Billed CD</td>
              <td>KVA</td>
              <td><input className="ba-input" value={inputs.billedCdKva} onChange={handleChange('billedCdKva')} /></td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I10) : '-'}</td>
              <td className="numeric-cell">-</td>
            </tr>
            <tr>
              <td>Units consumed</td>
              <td>Units</td>
              <td>
                <input
                  className="ba-input"
                  value={inputs.unitsConsumedUnits}
                  onChange={handleChange('unitsConsumedUnits')}
                  disabled={financeMapped?.unitsX != null}
                  title={financeMapped?.unitsX != null ? 'Auto-filled from Finance node (Units consumed X)' : ''}
                />
              </td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I11) : '-'}</td>
              <td className="numeric-cell">-</td>
            </tr>
            <tr>
              <td>Units consumed</td>
              <td>KVAH</td>
              <td>
                <input
                  className="ba-input"
                  value={inputs.unitsConsumedKvah}
                  readOnly
                  disabled
                  title={`Auto-derived: KVAH = Units / ${POWER_FACTOR}`}
                />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.F12) : '-'}</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.G12) : '-'}</td>
              <td className="numeric-cell">{calc ? format2(calc.oaUnitsRateH12) : '-'}</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I12) : '-'}</td>
              <td className="numeric-cell">-</td>
            </tr>

            <tr>
              <td>Demand charge</td>
              <td>INR</td>
              <td><input className="ba-input" value={inputs.demandChargeInr} onChange={handleChange('demandChargeInr')} /></td>
              <td className="numeric-cell">
                <input className="ba-input ba-input-small" value={inputs.demandChargeRateF13} onChange={handleChange('demandChargeRateF13')} placeholder="F13" />
              </td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I13) : '-'}</td>
              <td className="numeric-cell">{calc ? format2(calc.J13) : '-'}</td>
            </tr>

            <tr>
              <td>Wheeling charge</td>
              <td>INR</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.wheelingCharge) : '-'}</td>
              <td className="numeric-cell">{format2(wheelingRate)}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I14) : '-'}</td>
              <td className="numeric-cell">{format2(wheelingRate)}</td>
            </tr>

            <tr>
              <td>Energy charge</td>
              <td>INR</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.energyCharge) : '-'}</td>
              <td className="numeric-cell">
                <input className="ba-input ba-input-small" value={inputs.energyChargeRateF15} onChange={handleChange('energyChargeRateF15')} placeholder="F15" />
              </td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I15) : '-'}</td>
              <td className="numeric-cell">{calc ? format2(calc.J15) : '-'}</td>
            </tr>

            <tr>
              <td>TOD</td>
              <td>INR</td>
              <td>
                <input className="ba-input" value={inputs.todInr} onChange={handleChange('todInr')} disabled={financeMapped?.todChargesD != null} title={financeMapped?.todChargesD != null ? 'Auto-filled from Finance node (TOD Charges D)' : ''} />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.F16) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I16) : '-'}</td>
              <td className="numeric-cell">
                <input
                  className="ba-input ba-input-small"
                  value={todTariffModel?.hasData ? format2(todTariffModel.derivedJ16) : inputs.ebTodRateJ16}
                  onChange={handleChange('ebTodRateJ16')}
                  placeholder="J16"
                  disabled={!!todTariffModel?.hasData}
                  title={todTariffModel?.hasData ? 'Auto-calculated from TOD-wise tariff calculation (weighted avg - off-peak tariff)' : ''}
                />
              </td>
            </tr>

            <tr>
              <td>FAC</td>
              <td>INR</td>
              <td>
                <input className="ba-input" value={inputs.facInr} onChange={handleChange('facInr')} disabled={financeMapped?.facF != null} title={financeMapped?.facF != null ? 'Auto-filled from Finance node (FAC F)' : ''} />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.F17) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I17) : '-'}</td>
              <td className="numeric-cell">{calc ? format2(calc.J17) : '-'}</td>
            </tr>

            <tr>
              <td>ED</td>
              <td>INR</td>
              <td>
                <input className="ba-input" value={inputs.edInr} onChange={handleChange('edInr')} disabled={financeMapped?.electricityDutyC != null} title={financeMapped?.electricityDutyC != null ? 'Auto-filled from Finance node (Electricity Duty C)' : ''} />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.F18) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I18) : '-'}</td>
              <td className="numeric-cell">{calc ? format2(calc.J18) : '-'}</td>
            </tr>

            <tr>
              <td>Tax on sale</td>
              <td>INR</td>
              <td>
                <input className="ba-input" value={inputs.taxOnSaleInr} onChange={handleChange('taxOnSaleInr')} disabled={financeMapped?.taxE != null} title={financeMapped?.taxE != null ? 'Auto-filled from Finance node (Tax E)' : ''} />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.F19) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I19) : '-'}</td>
              <td className="numeric-cell">
                <input className="ba-input ba-input-small" value={inputs.ebTaxOnSaleRateJ19} onChange={handleChange('ebTaxOnSaleRateJ19')} placeholder="J19" />
              </td>
            </tr>

            <tr>
              <td>Tax Collection at Source</td>
              <td>INR</td>
              <td><input className="ba-input" value={inputs.tcsInr} onChange={handleChange('tcsInr')} /></td>
              <td className="numeric-cell">{calc ? format2(calc.F20) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">
                <input className="ba-input" value={inputs.ebTcsAmountI20} onChange={handleChange('ebTcsAmountI20')} placeholder="I20 (amount)" />
              </td>
              <td className="numeric-cell">
                <input className="ba-input ba-input-small" value={inputs.ebTcsRateJ20} onChange={handleChange('ebTcsRateJ20')} placeholder="J20" />
              </td>
            </tr>

            <tr>
              <td>Bulk Consumption rebate</td>
              <td>INR</td>
              <td>
                <input className="ba-input" value={inputs.bulkConsumptionRebateInr} onChange={handleChange('bulkConsumptionRebateInr')} disabled={financeMapped?.bulkConsumptionRebate != null} title={financeMapped?.bulkConsumptionRebate != null ? 'Auto-filled from Finance node (Rebate G component)' : ''} />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.F21) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">
                <input className="ba-input" value={inputs.ebBulkConsumptionRebateAmountI21} onChange={handleChange('ebBulkConsumptionRebateAmountI21')} placeholder="I21 (amount)" />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.J21) : '-'}</td>
            </tr>

            <tr>
              <td>Prompt payment discount</td>
              <td>INR</td>
              <td>
                <input className="ba-input" value={inputs.promptPaymentDiscountInr} onChange={handleChange('promptPaymentDiscountInr')} disabled={financeMapped?.promptPaymentDiscount != null} title={financeMapped?.promptPaymentDiscount != null ? 'Auto-filled from Finance node (Rebate G component)' : ''} />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.F22) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I22) : '-'}</td>
              <td className="numeric-cell">{calc ? format2(calc.J22) : '-'}</td>
            </tr>

            <tr>
              <td>Load factor incentive</td>
              <td>INR</td>
              <td>
                <input className="ba-input" value={inputs.loadFactorIncentiveInr} onChange={handleChange('loadFactorIncentiveInr')} disabled={financeMapped?.loadFactorIncentive != null} title={financeMapped?.loadFactorIncentive != null ? 'Auto-filled from Finance node (Rebate G component)' : ''} />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.F23) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">
                <input className="ba-input" value={inputs.ebLoadFactorIncentiveAmountI23} onChange={handleChange('ebLoadFactorIncentiveAmountI23')} placeholder="I23 (amount)" />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.J23) : '-'}</td>
            </tr>

            <tr>
              <td>Incremental consumption rebate</td>
              <td>INR</td>
              <td>
                <input className="ba-input" value={inputs.incrementalConsumptionRebateInr} onChange={handleChange('incrementalConsumptionRebateInr')} disabled={financeMapped?.incrementalConsumptionRebate != null} title={financeMapped?.incrementalConsumptionRebate != null ? 'Auto-filled from Finance node (Rebate G component)' : ''} />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.F24) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">
                <input className="ba-input" value={inputs.ebIncrementalConsumptionRebateAmountI24} onChange={handleChange('ebIncrementalConsumptionRebateAmountI24')} placeholder="I24 (amount)" />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.J24) : '-'}</td>
            </tr>

            <tr>
              <td>Regional subsidy</td>
              <td>INR</td>
              <td><input className="ba-input" value={inputs.regionalSubsidyInr} onChange={handleChange('regionalSubsidyInr')} /></td>
              <td className="numeric-cell">{calc ? format2(calc.F25) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? format2(calc.J25) : '-'}</td>
            </tr>

            <tr>
              <td>{resolveUniversalSubsidyLabel(financeInputs)}</td>
              <td>INR</td>
              <td>
                <input
                  className="ba-input"
                  value={inputs.universalSubsidyInr}
                  onChange={handleChange('universalSubsidyInr')}
                  disabled={financeMapped?.universalSubsidy != null}
                  title={financeMapped?.universalSubsidy != null ? 'Auto-filled from Finance node (Universal subsidy)' : ''}
                />
              </td>
              <td className="numeric-cell">{calc ? format2(calc.universalSubsidyRate) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? format2(calc.universalSubsidyRate) : '-'}</td>
            </tr>

            <tr className="ba-total-row">
              <td><b>Total Power cost</b></td>
              <td>INR</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.totalPowerCostE26) : '-'}</td>
              <td className="numeric-cell ba-highlight-yellow">{calc ? format2(calc.F26) : '-'}</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.G26) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I26) : '-'}</td>
              <td className="numeric-cell ba-highlight-yellow">{calc ? format2(calc.J26) : '-'}</td>
            </tr>
            <tr>
              <td><b>Replaceable Power cost</b></td>
              <td>INR/unit</td>
              <td className="numeric-cell ba-highlight-yellow">{calc ? format2(calc.replaceablePowerCostE27) : '-'}</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? fmtMaybeIndian(calc.I27) : '-'}</td>
              <td className="numeric-cell">{calc ? format2(calc.J27) : '-'}</td>
            </tr>
            <tr>
              <td>Savings on total power consumed</td>
              <td>INR/unit</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? format2(calc.J28) : '-'}</td>
            </tr>
            <tr>
              <td>Savings on OA units</td>
              <td>INR/unit</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">-</td>
              <td className="numeric-cell">{calc ? format2(calc.savingsOnOaUnits) : '-'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* TOD-wise tariff calculation */}
      {(todTariffModel?.rows || []).length ? (
        <div className="billing-analytics-subsection">
          <div className="billing-subtitle-row">
            <div className="billing-subtitle">TOD Wise Tariff Calculation</div>
            {typeof onIncludeTodWiseTariffInPdfChange === 'function' ? (
              <label className="summary-export-toggle billing-ops-export-toggle">
                <input
                  type="checkbox"
                  checked={includeTodWiseTariffInPdf}
                  onChange={(e) => onIncludeTodWiseTariffInPdfChange(e.target.checked)}
                />
                Include in PDF
              </label>
            ) : null}
          </div>
          <div className="summary-table-wrapper">
            <table className="summary-table billing-analytics-tod-tariff-table">
              <colgroup>
                <col style={{ width: '100px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '120px' }} />
                <col style={{ width: '130px' }} />
                <col style={{ width: '100px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>TIME SLOT</th>
                  <th>CONSUMPTION</th>
                  <th>DEMAND MET</th>
                  <th>GRID CONSUMPTION</th>
                  <th>TOD TARIFF</th>
                </tr>
              </thead>
              <tbody>
                {(todTariffModel.rows || []).map((r) => (
                  <tr key={r.key}>
                    <td>{r.label}</td>
                    <td className="numeric-cell">{fmtMaybeIndian(r.consumption, { empty: '0' })}</td>
                    <td className="numeric-cell">{fmtMaybeIndian(r.demandMet, { empty: '0' })}</td>
                    <td className="numeric-cell">{fmtMaybeIndian(r.gridConsumption, { empty: '0' })}</td>
                    <td className="numeric-cell">
                      <input
                        className="ba-input ba-input-small ba-input-yellow"
                        value={(inputs.todTariffsBySlot || {})[r.key] ?? ''}
                        onChange={handleTodTariffChange(r.key)}
                        placeholder="Rs/unit"
                      />
                    </td>
                  </tr>
                ))}
                <tr className="ba-total-row">
                  <td><b>Total</b></td>
                  <td className="numeric-cell"><b>{fmtMaybeIndian(todTariffModel.totals.totalConsumption, { empty: '0' })}</b></td>
                  <td className="numeric-cell"><b>{fmtMaybeIndian(todTariffModel.totals.totalDemandMet, { empty: '0' })}</b></td>
                  <td className="numeric-cell"><b>{fmtMaybeIndian(todTariffModel.totals.totalGridConsumption, { empty: '0' })}</b></td>
                  <td className="numeric-cell ba-highlight-yellow">{format2(todTariffModel.totals.weightedAvgTariff)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="billing-analytics-subgrid">
        <div className="billing-analytics-subsection">
          <div className="billing-subtitle-row">
            <div className="billing-subtitle">Operational Cost for Open Access</div>
            {typeof onIncludeOperationalCostInPdfChange === 'function' ? (
              <label className="summary-export-toggle billing-ops-export-toggle">
                <input
                  type="checkbox"
                  checked={includeOperationalCostInPdf}
                  onChange={(e) => onIncludeOperationalCostInPdfChange(e.target.checked)}
                />
                Include in PDF
              </label>
            ) : null}
          </div>
          <div className="summary-table-wrapper">
            <table className="summary-table billing-analytics-ops-table">
              <colgroup>
                <col style={{ width: '250px' }} />
                <col style={{ width: '70px' }} />
                <col style={{ width: '100px' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>ITEM</th>
                  <th>UOM</th>
                  <th>CHARGES</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Generation tariff</td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell">
                    <input className="ba-input" value={inputs.generationTariffInrPerUnit} onChange={handleChange('generationTariffInrPerUnit')} />
                  </td>
                </tr>
                <tr>
                  <td>Current Replacement Tariff</td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell">{calc ? format2(calc.replaceablePowerCostE27) : '-'}</td>
                </tr>
                <tr>
                  <td>Transmission charge in</td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell">{format2(transmissionRate)}</td>
                </tr>
                <tr>
                  <td>Transmission loss in Rs / unit</td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell">{calc ? format2(calc.oc.transmissionLoss) : '-'}</td>
                </tr>
                <tr>
                  <td>Wheeling charges in Rs unit</td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell">{format2(wheelingRate)}</td>
                </tr>
                <tr>
                  <td>Wheeling losses in Rs per unit</td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell">{calc ? format2(calc.oc.wheelingLoss) : '-'}</td>
                </tr>
                <tr>
                  <td>Banking Charge on Banked units</td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell">{calc ? format2(calc.oc.bankingCharge) : '-'}</td>
                </tr>
                <tr>
                  <td>Lapse unit charge</td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell">{calc ? format2(calc.oc.lapsedUnitCharge) : '-'}</td>
                </tr>
                <tr>
                  <td>O&amp;M, lease, Insurance and other expenses</td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell">{calc ? format2(calc.oc.omLeaseInsuranceOther) : '-'}</td>
                </tr>
                <tr className="ba-delivered-row">
                  <td><b>Delivered power cost at Consumption point</b></td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell ba-highlight-green">
                    {calc ? format2(calc.deliveredPowerCostH12) : '-'}
                  </td>
                </tr>
                <tr>
                  <td>Savings Per unit compared to DISCOM power</td>
                  <td>Rs/unit</td>
                  <td className="numeric-cell">-</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="billing-analytics-subsection">
          <div className="billing-subtitle">Notes</div>
          <div className="billing-notes">
            <div><b>Global variables used</b></div>
            <ul>
              <li>Wheeling charges: <b>{format2(wheelingRate)}</b></li>
              <li>Transmission charges: <b>{format2(transmissionRate)}</b></li>
              <li>Net settled units (annual): <b>{fmtMaybeIndian(netSettledUnitsAnnual, { empty: '0.00' })}</b></li>
            </ul>
            {!annualMetrics && (
              <div className="billing-warning">
                Banking metrics not available yet (run Banking node). OA calculations will show 0 until Net Settled Units is available.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingAnalyticsSection;
